import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Row, Col, Button, Input, Modal, Skeleton, Tabs, Space } from "antd";
import Select from "react-select";
import { faArrowAltCircleDown, faArrowCircleUp, faCompressArrowsAlt, faEdit, faTrash, faUniversity, faUserPlus, faUsers } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";

const BASE_STATE_CLIENT_CONTACT = { name: "", title: "", email: "", work_phone: "", cell_phone: "", additional_details: "" };

/*
Prop api
open: boolean, 
client_id: int,
force_edit: boolean
refresh: function

*/
function ModalQuickViewClient(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState(1200);
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [clientCategories, setClientCategories] = useState([]);
  const [editing, setEditing] = useState(false);

  const [clientContactDeleteModalOpen, setClientContactDeleteModalOpen] = useState(false);
  const [clientContactToDelete, setClientContactToDelete] = useState(null);

  const [clientDeactivateModalOpen, setClientDeactivateModalOpen] = useState(false);
  const [clientReactivateModalOpen, setClientReactivateModalOpen] = useState(false);

  const [clientContactAddModalOpen, setClientContactAddModalOpen] = useState(false);
  const [clientContactAddModalWidth, setClientContactAddModalWidth] = useState(900);

  const [clientContactToAdd, setClientContactToAdd] = useState(BASE_STATE_CLIENT_CONTACT);
  const [clientContactReordering, setClientContactReordering] = useState(false);

  const GetClient = async (id, show = false) => {
    setModalOpen(true);
    let response = await props.AxiosRequest("/api/clients/get", { cid: id });
    setClient(response.data.client);
  };
  const UpdateClient = async () => {
    if (client.name.length === 0) return props.MakeNotification("info", "Please enter the clients name.");
    if (isNaN(client.payment_term)) return props.MakeNotification("info", "Please make sure payment term is a valid number.");
    await props.AxiosRequest("/api/clients/update", { ...client });
    if (!props.quickViewProps.force_editing) setEditing(false);
    GetClient(client.id);
    props.setQuickViewProps({ ...props.quickViewProps, open: false, refresh: true });
  };
  const AddClientContact = async () => {
    if (clientContactToAdd.name.length === 0) return props.MakeNotification("info", "Please enter the contacts name.");
    await props.AxiosRequest("/api/client-contacts/insert", { ...clientContactToAdd, client_xref: client.id });
    // props.MakeNotification("success", "Successfully Added " + clientContactToAdd.name);
    GetClient(client.id);
    setClientContactAddModalOpen(false);
    setClientContactToAdd(BASE_STATE_CLIENT_CONTACT);
  };
  const UpdateClientContact = async (i) => {
    if (client.client_contacts[i].name.length === 0) return props.MakeNotification("info", "Please enter the contacts name.");
    await props.AxiosRequest("/api/client-contacts/update", { ...client.client_contacts[i] });
    setClient({
      ...client,
      client_contacts: client.client_contacts.map((m) => {
        return { ...m, editing: false };
      }),
    });
    props.setQuickViewProps({ ...props.quickViewProps, refresh: true });
    // GetClient(client.id);
    // if (this.props.refresh) this.props.refresh();
  };
  const DeleteClientContact = async () => {
    if (client.client_contacts.length === 1) return props.MakeNotification("warning", "You can not delete the last contact for a client.");
    await props.AxiosRequest("/api/client-contacts/delete", { id: clientContactToDelete.id, client_id: client.id });
    GetClient(client.id);
    setClientContactDeleteModalOpen(false);
    setModalOpen(false);
    setClientContactReordering(false);
  };
  const DeactivateClient = async () => {
    await props.AxiosRequest("/api/clients/deactivate", { id: client.id });
    setClientDeactivateModalOpen(false);
    setModalOpen(false);
    setClientContactReordering(false);
    props.setQuickViewProps({ ...props.quickViewProps, open: false, refresh: true, client_id: null });
  };

  const ReactivateClient = async () => {
    await props.AxiosRequest("/api/clients/reactivate", { id: client.id });
    setClientReactivateModalOpen(false);
    setModalOpen(false);
    setClientContactReordering(false);
    props.setQuickViewProps({ ...props.quickViewProps, open: false, refresh: true, client_id: null });
  };
  const ReorderClientContacts = async (reordered_client_contacts) => {
    await props.AxiosRequest("/api/client-contacts/update-order-bulk", { client_contacts: reordered_client_contacts });
    props.MakeNotification("success", "Successfully Updated Contacts");
  };

  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/user/get/all");
    setUsers(response.data.users);
  };
  const GetClientCategories = async () => {
    let response = await props.AxiosRequest("/api/client-categories/get");
    setClientCategories(
      response.data.data.map((m) => {
        return { ...m, label: m.name };
      }),
    );
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    let reordered_array = Array.from(client.client_contacts);
    const [removed] = reordered_array.splice(result.source.index, 1);
    reordered_array.splice(result.destination.index, 0, removed);
    reordered_array = reordered_array.map((m, i) => {
      return { ...m, seq: i + 1 };
    });
    setClient({ ...client, client_contacts: reordered_array });
    ReorderClientContacts(reordered_array);
    // this.setState({
    //   items
    // });
  };

  useEffect(() => {
    const resize = () => {
      const maxSize = Math.min(1200, window.innerWidth);
      setModalWidth(maxSize);
      const maxSize2 = Math.min(900, window.innerWidth);
      setClientContactAddModalWidth(maxSize2);
    };
    window.addEventListener("resize", resize);
  });

  useEffect(() => {
    if (props.quickViewProps.open && props.quickViewProps.client_id) {
      GetClient(props.quickViewProps.client_id);
    }
    setModalOpen(props.quickViewProps.open);
    if (props.quickViewProps.force_editing && editing !== props.quickViewProps.force_editing) setEditing(true);
  }, [props.quickViewProps.open, props.quickViewProps.client_id]);

  useEffect(() => {
    GetUsers();
    GetClientCategories();
  }, []);

  const quickViewTabs = [
    {
      key: "1",
      label: (
        <>
          <FontAwesomeIcon icon={faUniversity} className="mr-2" />
          Client Information
        </>
      ),
      children: (
        <>
          {/* Client Tab */}
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={12}>
              <h5 className="modal-title mb-3">Client Details</h5>
            </Col>
            <Col span={12} className="text-right pull-right">
              <div className="btn-group">
                <button type="button" className="btn btn-light mb-1 dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="true">
                  Actions&nbsp;
                </button>
                <div className="dropdown-menu" style={{ position: "absolute", inset: "0px auto auto 0px", margin: "0px", transform: "translate(0px, 35px)", dataPopperPlacement: "bottom-start" }}>
                  {!props.quickViewProps.force_editing ? (
                    <>
                      <span
                        className="dropdown-item clickable"
                        onClick={(e) => {
                          if (editing) GetClient(client.id);
                          setEditing(!editing);
                        }}
                      >
                        <FontAwesomeIcon className="pe-none" icon={faEdit} />
                        &nbsp; {editing ? "Cancel Editing Client" : "Edit Client"}
                      </span>
                      <div className="dropdown-divider"></div>
                    </>
                  ) : null}
                  {client?.deleted_at ? (
                    <span
                      className="dropdown-item clickable"
                      onClick={() => {
                        setClientReactivateModalOpen(true);
                      }}
                    >
                      <FontAwesomeIcon className="pe-none" icon={faArrowCircleUp} />
                      &nbsp; Reactivate Client
                    </span>
                  ) : null}
                  {!client?.deleted_at ? (
                    <span
                      className="dropdown-item clickable"
                      onClick={() => {
                        setClientDeactivateModalOpen(true);
                      }}
                    >
                      <FontAwesomeIcon className="pe-none" icon={faTrash} />
                      &nbsp; Deactivate Client
                    </span>
                  ) : null}
                </div>
              </div>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={12}>
              <label className="col-form-label-sm">Client Name *</label>
              <Input
                className="form-control"
                readOnly={!editing}
                value={client?.name}
                onChange={(e) => {
                  setClient({ ...client, name: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Client Categories</label>
              <Select
                allowClear={true}
                isMulti
                options={clientCategories}
                isDisabled={!editing}
                value={client?.client_categories}
                onChange={(e) => {
                  setClient({ ...client, client_categories: e });
                }}
              />
            </Col>
            <Col span={24}>
              <hr />
              {/* address */}
              <h5 className="modal-title mb-3">Client Information </h5>
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Home or Office Phone</label>
              <Input
                className="form-control"
                readOnly={!editing}
                value={client?.office_phone}
                onChange={(e) => {
                  setClient({ ...client, office_phone: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Fax</label>
              <Input
                className="form-control"
                readOnly={!editing}
                value={client?.fax}
                onChange={(e) => {
                  setClient({ ...client, fax: e.target.value });
                }}
              />
            </Col>
          </Row>
          <hr />
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={12}>
              <label className="col-form-label-sm">Address Line 1</label>
              <Input
                className="form-control"
                readOnly={!editing}
                value={client?.address_line_1}
                onChange={(e) => {
                  setClient({ ...client, address_line_1: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Address Line 2</label>
              <Input
                className="form-control"
                readOnly={!editing}
                value={client?.address_line_2}
                onChange={(e) => {
                  setClient({ ...client, address_line_2: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">City</label>
              <Input
                className="form-control"
                readOnly={!editing}
                value={client?.city}
                onChange={(e) => {
                  setClient({ ...client, city: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                <Col span={10}>
                  <label className="col-form-label-sm">State</label>
                  <Input
                    className="form-control"
                    readOnly={!editing}
                    value={client?.state}
                    onChange={(e) => {
                      setClient({ ...client, state: e.target.value });
                    }}
                  />
                </Col>
                <Col span={14}>
                  <label className="col-form-label-sm">Zip</label>
                  <Input
                    className="form-control"
                    readOnly={!editing}
                    value={client?.zip}
                    onChange={(e) => {
                      setClient({ ...client, zip: e.target.value });
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <hr />
          <h5 className="modal-title mb-3">Misc. Information</h5>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={12}>
              <label className="col-form-label-sm">Client Partner</label>
              <Select
                style={{ width: "100%" }}
                allowClear={true}
                options={props.MakeUserOptions(users)}
                isDisabled={!editing}
                value={client?.partner ? client?.partner : null}
                onChange={(e) => {
                  setClient({ ...client, partner: e, partner_id: e.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Client Manager</label>
              <Select
                style={{ width: "100%" }}
                allowClear={true}
                options={props.MakeUserOptions(users)}
                isDisabled={!editing}
                value={client?.manager ? client?.manager : null}
                onChange={(e) => {
                  setClient({ ...client, manager: e, manager_id: e.value });
                }}
              />
            </Col>
            <Col span={6}>
              {/* <label className="col-form-label-sm">Is Client Taxable?</label>
              <br />
              <input
                className="form-check-input"
                type="checkbox"
                checked={client?.is_taxable === 1 ? "checked" : ""}
                onChange={(e) => {
                  setClient({ ...client, is_taxable: e.target.checked ? 1 : 0 });
                }}
              /> */}
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Payment Term</label>
              <input
                className="form-control"
                readOnly={!editing}
                value={client?.payment_term}
                onChange={(e) => {
                  setClient({ ...client, payment_term: e.target.value });
                }}
              ></input>
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Additional Information</label>
              <textarea
                className="form-control"
                readOnly={!editing}
                value={client?.additional_details}
                onChange={(e) => {
                  setClient({ ...client, additional_details: e.target.value });
                }}
              ></textarea>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "2",
      label: (
        <>
          <FontAwesomeIcon icon={faUsers} className="mr-2" /> Client Contacts
        </>
      ),
      children: (
        <Row>
          <Col span={24} className="text-right">
            <div className="btn-group">
              <button type="button" className="btn btn-light mb-1 dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="true">
                Actions&nbsp;
              </button>
              <div className="dropdown-menu" style={{ position: "absolute", inset: "0px auto auto 0px", margin: "0px", transform: "translate(0px, 35px)", dataPopperPlacement: "bottom-start" }}>
                <span
                  className="dropdown-item clickable"
                  onClick={(e) => {
                    setClientContactAddModalOpen(true);
                  }}
                >
                  <FontAwesomeIcon className="pe-none" icon={faUserPlus} />
                  &nbsp; {clientContactAddModalOpen ? "Cancel Adding Contact" : "Add New Contact"}
                </span>
                {/* <span
                  className="dropdown-item clickable"
                  onClick={() => {
                    let updated_client_contacts = client.client_contacts;
                    updated_client_contacts[i] = { ...cc, editing: !cc.editing };
                    setClient({ ...client, client_contacts: updated_client_contacts });
                  }}
                >
                  <FontAwesomeIcon className="pe-none" icon={faEdit} />
                  &nbsp; {cc.editing ? "Cancel Editing Contact" : "Edit Contact"}
                </span> */}
                <span
                  className="dropdown-item clickable"
                  onClick={() => {
                    setClientContactReordering(!clientContactReordering);
                  }}
                >
                  <FontAwesomeIcon className="pe-none" icon={faCompressArrowsAlt} />
                  &nbsp; {clientContactReordering ? "Cancel Reordering Contacts" : "Reorder Contacts"}
                </span>
                {/* <div className="dropdown-divider"></div> */}
                {/* <span
                  className="dropdown-item clickable"
                  onClick={() => {
                    setClientContactToDelete(client.client_contacts[i]);
                    setClientContactDeleteModalOpen(true);
                  }}
                >
                  <FontAwesomeIcon className="pe-none" icon={faTrash} />
                  &nbsp; Delete Contact
                </span> */}
              </div>
            </div>
          </Col>
          <Col span={24}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="reorder-droppable">
                {(provided, snapshot) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {client?.client_contacts.map((cc, i) => {
                      return (
                        <Draggable key={cc.id} draggableId={cc.id + ""} index={i} isDragDisabled={!clientContactReordering}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <Card size="small" type="inner" style={clientContactReordering ? { borderStyle: "dashed", borderColor: "#3b7ddd" } : {}}>
                                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                                  <Col span={12}>
                                    <h5 className="modal-title mb-3">{i === 0 ? "Primary Contact" : "Contact"}</h5>
                                  </Col>
                                  <Col span={12} className="text-right pull-right">
                                    <div className="btn-group">
                                      <button type="button" className="btn btn-light mb-1 dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="true">
                                        Contact Actions&nbsp;
                                      </button>
                                      <div
                                        className="dropdown-menu"
                                        style={{ position: "absolute", inset: "0px auto auto 0px", margin: "0px", transform: "translate(0px, 35px)", dataPopperPlacement: "bottom-start" }}
                                      >
                                        <span
                                          className="dropdown-item clickable"
                                          onClick={() => {
                                            let updated_client_contacts = client.client_contacts;
                                            updated_client_contacts[i] = { ...cc, editing: !cc.editing };
                                            setClient({ ...client, client_contacts: updated_client_contacts });
                                          }}
                                        >
                                          <FontAwesomeIcon className="pe-none" icon={faEdit} />
                                          &nbsp; {cc.editing ? "Cancel Editing Contact" : "Edit Contact"}
                                        </span>
                                        {/* <span
                  className="dropdown-item clickable"
                  onClick={() => {
                    setClientContactReordering(!clientContactReordering);
                  }}
                >
                  <FontAwesomeIcon className="pe-none" icon={faCompressArrowsAlt} />
                  &nbsp; {clientContactReordering ? "Cancel Reordering Contacts" : "Reorder Contacts"}
                </span> */}
                                        <div className="dropdown-divider"></div>
                                        <span
                                          className="dropdown-item clickable"
                                          onClick={() => {
                                            setClientContactToDelete(client.client_contacts[i]);
                                            setClientContactDeleteModalOpen(true);
                                          }}
                                        >
                                          <FontAwesomeIcon className="pe-none" icon={faTrash} />
                                          &nbsp; Delete Contact
                                        </span>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                                  {/* title */}
                                  <Col span={12}>
                                    <label className="col-form-label-sm">Contact Name *</label>
                                    <input
                                      readOnly={!cc.editing}
                                      className="form-control"
                                      value={cc.name}
                                      onChange={(e) => {
                                        let cc = client.client_contacts;
                                        cc[i].name = e.target.value;
                                        setClient({ ...client, client_contacts: cc });
                                      }}
                                    ></input>
                                  </Col>
                                  <Col span={12}>
                                    <label className="col-form-label-sm">Title</label>
                                    <input
                                      readOnly={!cc.editing}
                                      className="form-control"
                                      value={cc.title}
                                      onChange={(e) => {
                                        let cc = client.client_contacts;
                                        cc[i].title = e.target.value;
                                        setClient({ ...client, client_contacts: cc });
                                      }}
                                    ></input>
                                  </Col>
                                  <Col span={24}>
                                    <hr />
                                  </Col>
                                  {/* address */}
                                </Row>
                                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                                  <Col span={24}>
                                    <h5 className="modal-title mb-2">Contact Information</h5>
                                  </Col>
                                  <Col span={24}>
                                    <label className="col-form-label-sm">Email</label>
                                    <input
                                      readOnly={!cc.editing}
                                      className="form-control"
                                      value={cc.email}
                                      onChange={(e) => {
                                        let cc = client.client_contacts;
                                        cc[i].email = e.target.value;
                                        setClient({ ...client, client_contacts: cc });
                                      }}
                                    ></input>
                                  </Col>
                                  <Col span={12}>
                                    <label className="col-form-label-sm">Work Phone</label>
                                    <input
                                      readOnly={!cc.editing}
                                      className="form-control"
                                      value={cc.work_phone}
                                      onChange={(e) => {
                                        let cc = client.client_contacts;
                                        cc[i].work_phone = e.target.value;
                                        setClient({ ...client, client_contacts: cc });
                                      }}
                                    ></input>
                                  </Col>
                                  <Col span={12}>
                                    <label className="col-form-label-sm">Cell Phone</label>
                                    <input
                                      readOnly={!cc.editing}
                                      className="form-control"
                                      value={cc.cell_phone}
                                      onChange={(e) => {
                                        let cc = client.client_contacts;
                                        cc[i].cell_phone = e.target.value;
                                        setClient({ ...client, client_contacts: cc });
                                      }}
                                    ></input>
                                  </Col>
                                  <Col span={24}>
                                    <label className="col-form-label-sm">Additional Details</label>
                                    <textarea
                                      readOnly={!cc.editing}
                                      className="form-control"
                                      value={cc.additional_details}
                                      onChange={(e) => {
                                        let cc = client.client_contacts;
                                        cc[i].additional_details = e.target.value;
                                        setClient({ ...client, client_contacts: cc });
                                      }}
                                    ></textarea>
                                  </Col>
                                  {cc.editing ? (
                                    <Col span={24} className="text-right pull-right mt-2">
                                      <Space>
                                        <Button
                                          onClick={() => {
                                            let updated_client_contacts = client.client_contacts;
                                            updated_client_contacts[i] = { ...cc, editing: !cc.editing };
                                            setClient({ ...client, client_contacts: updated_client_contacts });
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          type="primary"
                                          onClick={() => {
                                            UpdateClientContact(i);
                                          }}
                                        >
                                          Save Changes
                                        </Button>
                                      </Space>
                                    </Col>
                                  ) : null}
                                </Row>
                              </Card>
                              <br />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Col>
        </Row>
      ),
    },
  ];
  return (
    <>
      <Modal
        title={client?.name || ""}
        centered
        open={modalOpen}
        onCancel={() => {
          setEditing(false);
          GetClient(client.id);
          setModalOpen(false);
          setClientContactReordering(false);
          props.setQuickViewProps({ ...props.quickViewProps, open: false, client_id: null });
        }}
        footer={
          editing ? (
            <>
              <Button
                onClick={() => {
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  UpdateClient();
                }}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                onClick={() => {
                  setModalOpen(false);
                  props.setQuickViewProps({ open: false, client_id: null });
                }}
              >
                Close
              </Button>
            </>
          )
        }
        width={modalWidth}
      >
        {client ? <Tabs defaultActiveKey="1" items={quickViewTabs} /> : <Skeleton active />}
      </Modal>
      <Modal
        title={"Are you sure?"}
        centered
        open={clientContactDeleteModalOpen}
        onCancel={() => {
          setClientContactDeleteModalOpen(false);
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setClientContactDeleteModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              onClick={() => {
                DeleteClientContact();
              }}
            >
              Delete
            </Button>
          </>
        }
      >
        {clientContactToDelete ? <>Are you sure you want to delete the contact {clientContactToDelete?.name}?</> : <Skeleton active />}
      </Modal>
      <Modal
        title={"Are you sure?"}
        centered
        open={clientDeactivateModalOpen}
        onCancel={() => {
          setClientDeactivateModalOpen(false);
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setClientDeactivateModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              onClick={() => {
                DeactivateClient();
              }}
            >
              Deactivate
            </Button>
          </>
        }
      >
        <>Are you sure you want to deactivate the client {client?.name}?</>
      </Modal>
      <Modal
        title={"Are you sure?"}
        centered
        open={clientReactivateModalOpen}
        onCancel={() => {
          setClientReactivateModalOpen(false);
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setClientReactivateModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => {
                ReactivateClient();
              }}
            >
              Reactivate
            </Button>
          </>
        }
      >
        <>Are you sure you want to reactivate the client {client?.name}?</>
      </Modal>

      <Modal
        title={"Add A New Contact"}
        centered
        width={clientContactAddModalWidth}
        open={clientContactAddModalOpen}
        onCancel={() => {
          setClientContactAddModalOpen(false);
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setClientContactAddModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => {
                AddClientContact();
              }}
            >
              Add Contact
            </Button>
          </>
        }
      >
        {clientContactToAdd ? (
          <>
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              {/* title */}
              <Col span={12}>
                <label className="col-form-label-sm">Contact Name *</label>
                <input
                  className="form-control"
                  value={clientContactToAdd.name}
                  onChange={(e) => {
                    setClientContactToAdd({ ...clientContactToAdd, name: e.target.value });
                  }}
                ></input>
              </Col>
              <Col span={12}>
                <label className="col-form-label-sm">Title</label>
                <input
                  className="form-control"
                  value={clientContactToAdd.title}
                  onChange={(e) => {
                    setClientContactToAdd({ ...clientContactToAdd, title: e.target.value });
                  }}
                ></input>
              </Col>
              <Col span={24}>
                <hr />
              </Col>
              {/* address */}
            </Row>
            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              <Col span={24}>
                <h5 className="modal-title mb-2">Contact Information</h5>
              </Col>
              <Col span={24}>
                <label className="col-form-label-sm">Email</label>
                <input
                  className="form-control"
                  value={clientContactToAdd.email}
                  onChange={(e) => {
                    setClientContactToAdd({ ...clientContactToAdd, email: e.target.value });
                  }}
                ></input>
              </Col>
              <Col span={12}>
                <label className="col-form-label-sm">Work Phone</label>
                <input
                  className="form-control"
                  value={clientContactToAdd.work_phone}
                  onChange={(e) => {
                    setClientContactToAdd({ ...clientContactToAdd, work_phone: e.target.value });
                  }}
                ></input>
              </Col>
              <Col span={12}>
                <label className="col-form-label-sm">Cell Phone</label>
                <input
                  className="form-control"
                  value={clientContactToAdd.cell_phone}
                  onChange={(e) => {
                    setClientContactToAdd({ ...clientContactToAdd, cell_phone: e.target.value });
                  }}
                ></input>
              </Col>
              <Col span={24}>
                <label className="col-form-label-sm">Additional Details</label>
                <textarea
                  className="form-control"
                  value={clientContactToAdd.additional_details}
                  onChange={(e) => {
                    setClientContactToAdd({ ...clientContactToAdd, additional_details: e.target.value });
                  }}
                ></textarea>
              </Col>
            </Row>
          </>
        ) : (
          <Skeleton active />
        )}
      </Modal>
    </>
  );
}

export default ModalQuickViewClient;
